import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Skill, ApiError, PaginatedResponse } from '../../types/api';

interface UseSkillsState {
  skills: Skill[];
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
}

interface UseSkillsReturn extends UseSkillsState {
  fetchSkills: (params?: { page?: number; limit?: number }) => Promise<void>;
  getSkillById: (id: string) => Promise<Skill | null>;
  refresh: () => Promise<void>;
}

export const useSkills = (
  initialParams?: { page?: number; limit?: number },
  autoFetch: boolean = true
): UseSkillsReturn => {
  const [state, setState] = useState<UseSkillsState>({
    skills: [],
    loading: false,
    error: null,
    pagination: null,
  });

  const [queryParams, setQueryParams] = useState<{ page?: number; limit?: number }>(
    initialParams || { page: 1, limit: 50 }
  );

  const fetchSkills = useCallback(async (params?: { page?: number; limit?: number }) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response: PaginatedResponse<Skill> = await api.skills.getAll(
        params || queryParams
      );

      setState({
        skills: response.data,
        loading: false,
        error: null,
        pagination: response.pagination,
      });

      if (params) {
        setQueryParams(params);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApiError,
      }));
    }
  }, [queryParams]);

  const getSkillById = useCallback(async (id: string): Promise<Skill | null> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const skill = await api.skills.getById(id);
      setState((prev) => ({ ...prev, loading: false }));
      return skill;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApiError,
      }));
      return null;
    }
  }, []);

  const refresh = useCallback(() => {
    return fetchSkills(queryParams);
  }, [fetchSkills, queryParams]);

  useEffect(() => {
    if (autoFetch) {
      fetchSkills();
    }
  }, [autoFetch, fetchSkills]);

  return {
    ...state,
    fetchSkills,
    getSkillById,
    refresh,
  };
};
